import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { RARE, STANDARD } from '../../../../constants/shared/lootboxes/types'
import SetsPrize from '../setsPrize/SetsPrize'
import { useUser } from '../../../../redux/reducer/user'
import { setsContent, august2026ChangesData } from '../../../../constants/copyright'
import Back from '../../../features/back/Back'
import { useRequestsHandlerData } from '../../../../hooks/useRequestsHandlerData'
import AppLoader from '../../../features/appLoader/AppLoader'
import { CSSTransition, SwitchTransition } from 'react-transition-group'
import { useContent } from '../../../../redux/reducer/content'
import { useModal } from '../../../../hooks/useModal'
import useStateControls from '../../../../hooks/useStateControls'
import { useRequests } from '../../../../redux/reducer/requests'
import { useTranslation } from '../../../../translations'
import SetsHeader from '../setsHeader/SetsHeader'
import SetsRewards from '../setsRewards/SetsRewards'
import { useFeatureToggle } from '../../../../hooks/useFeatureToggle'
import classNames from 'classnames'
import { useErrorModalHandler } from '../../../../hooks/error/useErrorModalHandler'
import { useChangesAugust2026 } from '../../../../hooks/useChangesAugust2026'

const Sets = React.forwardRef(({ callbacks }, ref) => {
  const {
    profile,
    profile: { gacha_event_cash, money, beauties },
  } = useUser()
  const {
    lootBoxes: { standard, rare },
    activeLootBoxPrize,
    previousStates,
  } = useContent()
  const {
    authData: { isWeb },
  } = useRequests()
  const {
    title,
    tabs: setsTabsContent,
    gradeData: gradeDataValue,
    button,
    boxTitle,
    boxText,
    openBtn,
    rewards,
    buyData,
    availablePrevStates,
    modalBuyTitle,
    toggleData,
  } = setsContent

  const { setState } = useStateControls()
  const { addModal } = useModal()
  const { t } = useTranslation()
  const showErrorModal = useErrorModalHandler()

  const lastOpenLootBoxIdRef = useRef()

  const [activeTab, setActiveTab] = useState(0)

  const activeTabToggleSetter = {
    common: () => setActiveTab(0),
    rare: () => setActiveTab(1),
  }

  const gameTamagotchiRareBox = useFeatureToggle('gameTamagotchiRareBox')
  const gameTamagotchiBeauties = useFeatureToggle('gameTamagotchiBeauties')

  const isTabsVisible = useMemo(() => {
    return gameTamagotchiBeauties ? false : !gameTamagotchiRareBox
  }, [gameTamagotchiBeauties, gameTamagotchiRareBox])

  const tabs = useMemo(() => setsTabsContent(gameTamagotchiBeauties), [gameTamagotchiBeauties])

  const displayTab = useMemo(() => {
    if (!gameTamagotchiBeauties) return activeTab
    return rare?.bought > 0 ? 1 : 0
  }, [gameTamagotchiBeauties, activeTab, rare?.bought])

  const activeLootBox = displayTab === 0 ? standard : rare

  const activeCash = useMemo(() => {
    if (!gameTamagotchiBeauties) {
      return activeTab === 0 ? money : gacha_event_cash
    }

    return beauties
  }, [activeTab, money, gacha_event_cash, beauties, gameTamagotchiBeauties])

  const hasEnoughCashToBuyBox = useMemo(() => {
    return activeCash >= activeLootBox?.cost
  }, [activeLootBox, activeCash])

  const gradeDataFiltered = useMemo(() => {
    const excludeTypes = ['beauties']
    if (!gameTamagotchiBeauties) {
      return gradeDataValue().filter((item) => !excludeTypes.includes(item.type))
    }

    return gradeDataValue().filter((item) => item.type === 'beauties')
  }, [gameTamagotchiBeauties])

  const gradeData = useMemo(() => {
    const beautLoot = gameTamagotchiBeauties
    const row = beautLoot ? 0 : activeTab
    const gradeCopyrightData = JSON.parse(JSON.stringify(gradeDataFiltered[row]))
    if (activeLootBox?.bought) gradeCopyrightData.prize.button = openBtn

    const fullGrade = gradeDataValue()
    const titleIx = beautLoot ? fullGrade.findIndex(({ type }) => type === 'beauties') : activeTab

    return {
      ...gradeCopyrightData,
      cost: [{ ...gradeCopyrightData.cost[0] }],
      prize: {
        ...gradeCopyrightData.prize,
        title: fullGrade[titleIx]?.prize?.title,
      },
    }
  }, [activeLootBox, activeTab, gradeDataFiltered, gameTamagotchiBeauties])

  const toBack = () => {
    const reversed = [...previousStates].reverse()
    const prevState = reversed.find((state) => availablePrevStates.includes(state)) ?? 'main'
    setState(prevState)
  }

  // событие при покупки бокса
  // вызывается только при условиях:
  // 1. лимит покупки за день не превышен
  // 2. есть не открытые боксы
  const buyLootBox = useCallback(() => {
    if (activeLootBox?.limit <= 0 && !activeLootBox.bought) return
    if (gameTamagotchiBeauties && displayTab === 1 && !activeLootBox?.bought) return

    const {
      buttons: { can },
    } = buyData

    const props = {
      title: modalBuyTitle(displayTab, activeLootBox.bought, t),
      buttons: can.map((btnData) => {
        const { attr, action } = btnData
        if (!['buy'].includes(action)) return btnData
        return {
          ...btnData,
          attr: { ...attr, text: activeLootBox.bought ? t('other.open') : t('other.buy') },
        }
      }),
      lootBoxesCount: activeLootBox.bought,
      callbacks: {
        buy: () => {
          lastOpenLootBoxIdRef.current = activeLootBox?.id
          callbacks.buyLootBoxById(activeLootBox?.id)
        },
      },
    }

    addModal({ type: 'buyModal', props })
  }, [activeLootBox, displayTab, callbacks, addModal, t, buyData, gameTamagotchiBeauties])

  const isPending = useRequestsHandlerData([
    { address: 'requestsGachaList' },
    {
      address: 'requestsGachaOpen',
      fulfilled: (payload) => {
        const prize = payload?.prizes?.[0]
        if (!prize) return

        const openedId = lastOpenLootBoxIdRef.current
        const lootBoxPrizeData = {
          type: openedId === rare?.id ? RARE : STANDARD,
          id: prize?.id,
          prizeData: prize,
        }

        callbacks.setPendingGachaReward({
          id: `${prize?.id}-${Date.now()}`,
          openPayload: payload,
        })
        callbacks.setActiveLootBoxPrize(lootBoxPrizeData)
      },
      rejected: (data) => {
        const { response } = data

        showErrorModal(response.data)
      },
    },
  ])

  useEffect(() => {
    const { id, type, prizeData } = activeLootBoxPrize
    if (
      [
        [id, 'number'],
        [type, 'string'],
        [prizeData, 'object'],
      ].every(([value, type]) => typeof value == type)
    )
      setState('wheel')
  }, [activeLootBoxPrize])

  useEffect(() => {
    callbacks.getLootBoxesList()

    if (gameTamagotchiBeauties) {
      callbacks.getBeautiesBalance()
    }
  }, [])

  useChangesAugust2026(august2026ChangesData.sets.serviceDataKey)

  return (
    <section
      className={classNames('sets', {
        app: !isWeb,
        sets_noInfo: button,
        'tamagotchi-2026': true,
      })}
      ref={ref}
    >
      <Back onClick={toBack} />
      <div className={`sets__content`}>
        <SetsHeader
          title={title}
          activeTab={activeTab}
          toggleData={toggleData}
          toggleSetter={activeTabToggleSetter}
          showTabs={isTabsVisible}
          gradeData={gradeData}
          activeCash={activeCash}
          button={button}
        />

        <SwitchTransition>
          <CSSTransition
            key={`item-${displayTab}`}
            timeout={500}
            classNames={'sets__main'}
          >
            <div className={'sets__main'}>
              <SetsPrize
                name={tabs[displayTab].title}
                activeTab={displayTab}
                callback={buyLootBox}
                boxText={boxText}
                boxTitle={boxTitle}
                isEnoughCahs={hasEnoughCashToBuyBox}
                {...activeLootBox}
                {...gradeData?.prize}
              />

              <SetsRewards
                gradeData={gradeData}
                activeTab={displayTab}
                rewards={rewards}
              />
            </div>
          </CSSTransition>
        </SwitchTransition>
      </div>
      {isPending && <AppLoader />}
    </section>
  )
})
export default Sets
Sets.displayName = 'Sets'
Sets.propTypes = {}
